import React, { useState, useRef, useCallback } from 'react';
import { analyzeReadme } from '../services/apiService';
import './ReadmeUpload.css';

interface ReadmeUploadProps {
  onAnalysisComplete: (result: any) => void;
  onError: (error: string) => void;
  isLoading?: boolean;
}

interface FileInfo {
  file: File;
  name: string;
  size: number;
  type: string;
  preview?: string;
}

export const ReadmeUpload: React.FC<ReadmeUploadProps> = ({
  onAnalysisComplete,
  onError,
  isLoading = false
}) => {
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 1024 * 1024; // 1MB
  const ALLOWED_EXTENSIONS = ['.md', '.markdown'];
  const ALLOWED_MIME_TYPES = ['text/markdown', 'text/plain', 'application/octet-stream'];

  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size === 0) {
      return 'File is empty. Please select a README file with content.';
    }

    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 1MB.';
    }

    // Check file extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      return 'Invalid file type. Only .md and .markdown files are allowed.';
    }

    return null;
  };

  const processFile = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }

    // Create file info
    const fileInfo: FileInfo = {
      file,
      name: file.name,
      size: file.size,
      type: file.type || 'text/markdown'
    };

    // Add preview for small files
    if (file.size < 1000) {
      try {
        const text = await file.text();
        fileInfo.preview = text.substring(0, 200);
      } catch (err) {
        // Preview not critical, continue without it
      }
    }

    setSelectedFile(fileInfo);
    setError(null);
  };

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Find first valid file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validationError = validateFile(file);
      if (!validationError) {
        await processFile(file);
        return;
      }
    }

    // If no valid file found, show error for first file
    const firstFile = files[0];
    const validationError = validateFile(firstFile);
    setError(validationError || 'No valid README file found.');
    setSelectedFile(null);
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event.target.files);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoading && !isAnalyzing) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (isLoading || isAnalyzing) return;

    const files = event.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) return;

    setIsAnalyzing(true);
    setError(null);

    try {
      const result = await analyzeReadme(selectedFile.file);
      onAnalysisComplete(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Analysis failed';
      setError(`Analysis failed: ${errorMessage}`);
      onError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 bytes';
    const k = 1024;
    const sizes = ['bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const dropZoneClasses = [
    'readme-upload-zone',
    isDragOver ? 'drag-over' : '',
    isLoading ? 'disabled' : '',
    selectedFile ? 'has-file' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className="readme-upload-container">
      <div className="readme-upload-header">
        <h3>Upload README.md</h3>
        <p>Upload your API's README file to automatically extract metadata</p>
      </div>

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-message">Processing... Please wait.</div>
        </div>
      )}

      <div
        className={dropZoneClasses}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="readme-drop-zone"
      >
        {!selectedFile ? (
          <div className="upload-prompt">
            <div className="upload-icon">📄</div>
            <div className="upload-text">
              <strong>Drag and drop your README.md file here</strong>
              <br />
              or <button 
                type="button" 
                className="link-button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
              >
                click to select
              </button>
            </div>
            <div className="upload-requirements">
              Supports .md and .markdown files (max 1MB)
            </div>
          </div>
        ) : (
          <div className="file-info">
            <div className="file-details">
              <div className="file-name">{selectedFile.name}</div>
              <div className="file-meta">
                {formatFileSize(selectedFile.size)} • {selectedFile.type.includes('markdown') ? 'Markdown' : 'Text'}
              </div>
              {selectedFile.preview && (
                <div className="file-preview">
                  <code>{selectedFile.preview}...</code>
                </div>
              )}
            </div>
            <button
              type="button"
              className="remove-file-button"
              onClick={handleRemoveFile}
              aria-label="Remove file"
              disabled={isAnalyzing}
            >
              ✕
            </button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          onChange={handleInputChange}
          className="file-input"
          aria-label="Upload README file"
          disabled={isLoading}
        />
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {selectedFile && (
        <div className="file-selected-message" aria-live="polite">
          Selected: {selectedFile.name}
        </div>
      )}

      <div className="upload-actions">
        <button
          type="button"
          className="analyze-button"
          onClick={handleAnalyze}
          disabled={!selectedFile || isAnalyzing || isLoading}
        >
          {isAnalyzing ? (
            <>
              <span className="loading-spinner" role="progressbar" aria-label="Analyzing"></span>
              Analyzing...
            </>
          ) : (
            'Analyze README'
          )}
        </button>
      </div>
    </div>
  );
};
